import { Directive, input } from '@angular/core'
import { TranslatorAccessService } from './translator-access.service'

/**
 * Rend une zone non interactive en édition de traduction.
 *
 *   <!-- champ précis, verrouillé s'il n'est pas dans la déclaration partagée -->
 *   <div [mosLockedForTranslation]="locked('npcId')">…</div>
 *
 *   <!-- zone entière sans notion de champ (pistes de timeline, onglets de l'éditeur…) -->
 *   <div [mosLockedForTranslation]="translatorAccess.isStructureLocked()">…</div>
 *
 * Sert aux widgets qui échappent au verrouillage des FormControl : mos-npc-select-list,
 * mos-npc-avatar-select et les bascules maison sont liés en [value]/(valueChange) et non via un
 * ControlValueAccessor, donc désactiver leur FormControl ne les rendait pas inertes - on pouvait cliquer
 * dessus, et patchValue() s'applique sans broncher sur un contrôle désactivé.
 *
 * La directive prend délibérément un BOOLÉEN et non le FormControl : lié au contrôle, l'état ne serait
 * pas réactif (la référence ne change jamais et `disable({ emitEvent: false })` n'émet rien - c'est
 * justement ce qui évite la boucle NG0103), si bien que le verrou ne s'appliquait qu'au hasard d'un autre
 * cycle de détection. `translationFieldLock` lit des signaux, lui, donc la liaison se réévalue.
 *
 * `inert` d'abord : il coupe aussi le focus clavier, là où les trois `.readonly` maison qu'il remplace
 * laissaient la zone atteignable en tabulation. `pointer-events` par-dessus, en second rideau : des clics
 * passaient encore sur les sélecteurs de point de vue et de type de scène alors que le verrou était bien
 * calculé - je n'ai pas pu établir pourquoi `inert` n'y suffisait pas, et un verrou de présentation ne se
 * débogue pas depuis un test (jsdom n'implémente pas le comportement d'`inert`, il n'en garde que
 * l'attribut). Ce qui protège vraiment la donnée n'est de toute façon pas ici mais dans le code :
 * SceneEditorService.updateScene refuse tout changement de structure.
 */
@Directive({
	selector: '[mosLockedForTranslation]',
	host: {
		'[attr.inert]': 'locked() ? "" : null',
		'[attr.aria-disabled]': 'locked() ? "true" : null',
		'[style.pointer-events]': 'locked() ? "none" : null',
		'[style.opacity]': 'locked() ? 0.5 : null',
	},
})
export class LockedForTranslationDirective {
	readonly locked = input.required<boolean>({ alias: 'mosLockedForTranslation' })
}

/**
 * Construit le prédicat de verrouillage d'un formulaire à partir de la MÊME déclaration que celle qui
 * borne l'écriture côté serveur (`*_I18N_WRITE_FIELDS`) et que consomme lockNonI18nControls.
 *
 *   protected readonly locked = translationFieldLock(SCENE_DATA_DIALOG_I18N_WRITE_FIELDS, this.translatorAccess)
 *
 * Un chemin absent de la déclaration - y compris par faute de frappe - est verrouillé : l'erreur retire
 * des droits, elle n'en accorde jamais.
 */
export function translationFieldLock(
	writablePaths: readonly string[],
	translatorAccess: TranslatorAccessService,
): (path: string) => boolean {
	return (path) =>
		translatorAccess.isStructureLocked() && !(translatorAccess.canWriteI18nText() && writablePaths.includes(path))
}
